import * as reportsSdk from '@adsk/acs-reports-sdk';
import {
  getContainerId,
  getCurrentUserId,
  getCurrentViewType,
  getFetchParams,
  getFieldIssueTypesAndSubTypesIdToTitleMap,
  getOxygenMappings,
  getCurrentPreviewIssueId,
  getIssuesCollection,
  getProjectId,
  getCurrentProject
} from '../selectors';
import {trackEvent} from './analytics-actions';
import {
  downloadReportAnalytics,
  exportList,
  getAssigneeTypeByAssigneeId,
  viewTypeToScreenType
} from '../../utils/analytics-helpers';
import {reportFormats} from '../../constants/analytics';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {ISSUE_FILTER_PROPERTIES, ISSUE_HIDDEN_FILTER_PROPERTIES} from '../../constants/issue-query-params';
import {formatLocalDateTimeRange, formatUtcDateRange} from '../../utils/date-helpers';
import {ENV_CONFIGS} from '../../constants/envs';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import i18n from '../../utils/i18n';
import lodashGet from 'lodash/get';
import {NotificationLevels, NotificationTypes} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import {
  REPORT_COMPLETE_STATUS,
  REPORT_POLLING_INTERVAL,
  REPORT_SYNC_TIMEOUT_SECONDS_DEFAULT
} from '../../constants/ReportService';
import {showNotificationAction} from './index';
import {TIME_UNIT} from '../../constants/common';
import {isFedrampRegion} from '../../utils/region-helpers';
import {getToken} from '../../utils/getToken';

export const ISSUES_CREATE_REPORT_START = 'ISSUES_CREATE_REPORT_START';
export const ISSUES_CREATE_REPORT_API_FULFILLED = 'ISSUES_CREATE_REPORT_API_FULFILLED';
export const ISSUES_CREATE_REPORT_API_REJECTED = 'ISSUES_CREATE_REPORT_API_REJECTED';
export const CSV_LIMIT_EXCEEDED = 'CSV_LIMIT_EXCEEDED';
export const BCF_EXPORT_START = 'BCF_EXPORT_START';

export const csvLimitExceeded = () => ({type: CSV_LIMIT_EXCEEDED});

const createReportStart = () => ({type: ISSUES_CREATE_REPORT_START});

const createReportSuccess = ({reportData, reportMetaData}) => dispatch => {
  dispatch({type: ISSUES_CREATE_REPORT_API_FULFILLED});
  dispatch(
    showNotificationAction({
      type: NotificationTypes.TOAST,
      level: reportMetaData.displayId ? NotificationLevels.INFO : NotificationLevels.SUCCESS,
      message: reportMetaData.displayId
        ? i18n.t('export.exportSinglePDFAsyncMessage', {
          identifier: reportMetaData.displayId,
          issueType: TYPE_QUALITY_ISSUES
        })
        : i18n.t('export.exportSuccess', {
          reportTitle: lodashGet(reportData, 'title')
        })
    })
  );
};

const createReportFailure = ({failureMessage}) => dispatch => {
  dispatch({type: ISSUES_CREATE_REPORT_API_REJECTED});
  dispatch(
    showNotificationAction({
      type: NotificationTypes.SNACKBAR,
      level: NotificationLevels.ERROR,
      message: failureMessage
    })
  );
};

const trackDownloadIssueEvent = issue => (dispatch, getState) => {
  const state = getState();
  const {
    [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: assignedTo,
    [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]: assignedToType,
    [ISSUE_ATTRIBUTE_NAMES.STATUS]: status,
    [ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]: issueTypeId,
    [ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]: issueSubtypeId
  } = issue;

  // Analytics - Send "Download Issue" event
  const currentUserId = getCurrentUserId(state);
  const currentViewType = getCurrentViewType(state);
  const oxygen = getOxygenMappings(state);
  const assignee = getAssigneeTypeByAssigneeId(assignedTo, currentUserId, oxygen, assignedToType);
  const origin = viewTypeToScreenType(currentViewType);
  const issueTypesAndSubTypesTitleMap = getFieldIssueTypesAndSubTypesIdToTitleMap(state);
  const issueTypeTitle = issueTypesAndSubTypesTitleMap[issueTypeId];
  const issueSubTypeTitle = issueTypesAndSubTypesTitleMap[issueSubtypeId];

  dispatch(trackEvent(downloadReportAnalytics(assignee, origin, status, issueTypeTitle, issueSubTypeTitle)));
};

const isReportReady = reportInfo => reportInfo?.status === REPORT_COMPLETE_STATUS;

const getRequestConfig = ({dispatch, getState}) => ({
  ...ENV_CONFIGS.REPORT_SDK_CONFIG,
  getAccessToken: () => getToken({dispatch, getState})
});

export const createReportAction = (reportData, failureMessage, reportMetaData = {}) => async (dispatch, getState) => {
  const state = getState();
  const requestConfig = getRequestConfig({dispatch, getState});
  const {id: projectId} = getCurrentProject(state);

  dispatch(createReportStart());

  return reportsSdk
    .createReport({...reportData, projectId}, requestConfig)
    .then(() => {
      dispatch(createReportSuccess({reportData, reportMetaData}));
    })
    .catch(() => {
      dispatch(createReportFailure({failureMessage}));
    });
};

export const createReportSyncAction = (reportData, issue, fileName, asyncMessage, failureMessage) => async (
  dispatch,
  getState
) => {
  const state = getState();
  const synchronousReportTimeLimit =
    (ENV_CONFIGS.REPORT_SYNC_TIMEOUT_SECONDS ?? REPORT_SYNC_TIMEOUT_SECONDS_DEFAULT) * TIME_UNIT.SECOND;
  const requestConfig = getRequestConfig({dispatch, getState});
  const {id: projectId} = getCurrentProject(state);

  if (!isFedrampRegion) {
    //No tracking in Fedramp region
    dispatch(trackDownloadIssueEvent(issue));
  }

  // Dispatch events for starting create report process and relevant notification message
  dispatch(createReportStart());

  try {
    const {id} = await reportsSdk.createReport({...reportData, projectId}, requestConfig);
    const pendingReport = reportsSdk.pollReport(
      {
        id,
        projectId,
        timeout: synchronousReportTimeLimit,
        interval: REPORT_POLLING_INTERVAL,
        skipNotificationsOnComplete: true
      },
      requestConfig
    );

    dispatch(
      showNotificationAction({
        type: NotificationTypes.TOAST,
        level: NotificationLevels.SUCCESS,
        message: asyncMessage,
        autoDismiss: 0.5,
        asyncOperation: pendingReport,
        closeButtonTooltipText: i18n.t('notifications.downloadWillContinue')
      })
    );

    const report = await pendingReport;

    if (isReportReady(report)) {
      const blob = await reportsSdk.downloadReport({id: report.id, projectId}, requestConfig);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();

      dispatch({type: ISSUES_CREATE_REPORT_API_FULFILLED});
    } else {
      dispatch(createReportFailure({failureMessage}));
    }
  } catch (err) {
    if (err instanceof reportsSdk.PollDurationError) {
      dispatch(createReportSuccess({reportData, reportMetaData: issue}));
    } else {
      dispatch(createReportFailure({failureMessage}));
    }
  }
};

export const createBcfFileAction = () => (dispatch, getState) => {
  const paramToCommaList = param => (param.constructor === Array ? param.join(',') : param);

  const state = getState();
  const containerId = getContainerId(state);
  const viewType = getCurrentViewType(state);

  dispatch(trackEvent(exportList(reportFormats.BCF, viewType)));

  const fetchParams = getFetchParams(state, TYPE_QUALITY_ISSUES);
  const {targetUrn, documentVersion} = fetchParams;
  const queryParams = {...fetchParams.queryParams, ...fetchParams.hiddenQueryParams};
  const filter = {};

  let queryString = '';
  queryString += containerId ? `containerId=${containerId}` : '';

  if (queryParams[ISSUE_HIDDEN_FILTER_PROPERTIES.ID] && queryParams[ISSUE_HIDDEN_FILTER_PROPERTIES.ID].length > 0) {
    filter['filter[id]'] = paramToCommaList(queryParams[ISSUE_HIDDEN_FILTER_PROPERTIES.ID]);
  }
  if (targetUrn) {
    filter['filter[target_urn]'] = `${targetUrn}${documentVersion ? `@${documentVersion}` : ''}`;
  }
  if (
    queryParams[ISSUE_FILTER_PROPERTIES.STATUS_FILTER] &&
    queryParams[ISSUE_FILTER_PROPERTIES.STATUS_FILTER].length > 0
  ) {
    filter['filter[status]'] = paramToCommaList(queryParams[ISSUE_FILTER_PROPERTIES.STATUS_FILTER]);
  }
  if (
    queryParams[ISSUE_FILTER_PROPERTIES.ISSUE_TYPE_FILTER] &&
    queryParams[ISSUE_FILTER_PROPERTIES.ISSUE_TYPE_FILTER].length > 0
  ) {
    filter['filter[ng_issue_type_id]'] = paramToCommaList(queryParams[ISSUE_FILTER_PROPERTIES.ISSUE_TYPE_FILTER]);
  }
  if (
    queryParams[ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER] &&
    queryParams[ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER].length > 0
  ) {
    filter['filter[ng_issue_subtype_id]'] = paramToCommaList(
      queryParams[ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER]
    );
  }
  if (
    queryParams[ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER] &&
    queryParams[ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER].length > 0
  ) {
    filter['filter[created_by]'] = paramToCommaList(queryParams[ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER]);
  }
  if (queryParams[ISSUE_FILTER_PROPERTIES.OWNER_FILTER]?.length > 0) {
    filter['filter[owner]'] = paramToCommaList(queryParams[ISSUE_FILTER_PROPERTIES.OWNER_FILTER]);
  }
  if (
    queryParams[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER] &&
    queryParams[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER].length > 0
  ) {
    filter['filter[assigned_to]'] = paramToCommaList(queryParams[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER]);
  }
  if (
    queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER] ||
    queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]
  ) {
    filter['filter[created_at]'] = formatLocalDateTimeRange(
      queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER],
      queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]
    );
  }
  if (
    queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER] ||
    queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]
  ) {
    filter['filter[due_date]'] = formatUtcDateRange(
      queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER],
      queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]
    );
  }
  if (
    queryParams[ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER] &&
    queryParams[ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER].length > 0
  ) {
    filter['filter[root_cause_id]'] = paramToCommaList(queryParams[ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER]);
  }
  if (queryParams[ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER]) {
    filter['filter[lbs_location]'] = queryParams[ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER];
  }
  if (queryParams[ISSUE_FILTER_PROPERTIES.SEARCH]) {
    filter['filter[title]'] = queryParams[ISSUE_FILTER_PROPERTIES.SEARCH];
  }
  if (Object.keys(filter).length) {
    queryString += '&filter=';
    Object.keys(filter).forEach((filterKey, i) => {
      if (i > 0) {
        queryString += '&';
      }
      queryString += encodeURIComponent(`${filterKey}=${filter[filterKey]}`);
    });
  }

  window.open(`${ENV_CONFIGS.BCF_EXPORT_BASE_URL}/v1/export/?${queryString}`);
};

export const getModelTypeAndUnit = () => (dispatch, getState, {pushpinsControl}) => ({
  modelUnit: pushpinsControl?.getModelUnit(),
  modelType: pushpinsControl?.getModelType()
});

export const createNewBcfFileAction = (fileName, type) => (dispatch, getState, {pushpinsControl, issuesV2Api}) => {
  const modelUnit = pushpinsControl.getModelUnit();
  const modelType = pushpinsControl.getModelType();
  const state = getState();
  const viewType = getCurrentViewType(state);
  const issues = Object.keys(getIssuesCollection(state)).length;

  dispatch(trackEvent(exportList(reportFormats.BCF_V2, viewType, type, issues)));
  const currentPreviewIssueId = getCurrentPreviewIssueId(state);
  const query = {...getFetchParams(state)};
  if (currentPreviewIssueId) {
    query.queryParams['id'] = currentPreviewIssueId;
  }
  const currentUserId = getCurrentUserId(state);
  const projectId = getProjectId(state);
  return issuesV2Api.exportBcf(modelUnit, modelType, projectId, currentUserId, fileName, query);
};
